import { cn } from '../../lib/utils';
import { useTranslation } from '../../context/LanguageContext';
import type { TranslationKey } from '../../i18n';
import type { ExamType } from '../../types/wizard';
import { PenLine, List, Check } from 'lucide-react';

interface ExamTypeSelectorProps {
  value: ExamType | null;
  onChange: (type: ExamType) => void;
}

const OPTIONS: {
  value: ExamType;
  labelKey: TranslationKey;
  descriptionKey: TranslationKey;
  icon: React.ReactNode;
  gradient: string;
}[] = [
  {
    value: 'written',
    labelKey: 'common.written',
    descriptionKey: 'wizard.examTypeWrittenDesc',
    icon: <PenLine className="h-5 w-5" />,
    gradient: 'from-brand-500 to-violet-600',
  },
  {
    value: 'mcq',
    labelKey: 'common.mcq',
    descriptionKey: 'wizard.examTypeMcqDesc',
    icon: <List className="h-5 w-5" />,
    gradient: 'from-sky-500 to-blue-600',
  },
];

export function ExamTypeSelector({ value, onChange }: Readonly<ExamTypeSelectorProps>) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {OPTIONS.map((opt) => {
        const isSelected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'relative flex flex-col items-start gap-3 rounded-2xl border-2 p-5 text-left transition-all duration-200 focus:outline-none group',
              isSelected
                ? 'border-violet-500/50 bg-violet-500/10 shadow-glow'
                : 'border-slate-700 bg-slate-700/50 hover:border-violet-500/40 hover:bg-violet-500/10 hover:-translate-y-0.5 hover:shadow-card-hover'
            )}
          >
            <div
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-200 group-hover:scale-105',
                isSelected
                  ? `bg-gradient-to-br ${opt.gradient} text-white shadow-md`
                  : 'bg-slate-700 text-slate-400'
              )}
            >
              {opt.icon}
            </div>
            <div>
              <p className={cn('font-semibold', isSelected ? 'text-violet-300' : 'text-white')}>
                {t(opt.labelKey)}
              </p>
              <p className="text-sm text-slate-500 mt-0.5">{t(opt.descriptionKey)}</p>
            </div>
            {isSelected && (
              <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
